/**
 * Presentation of supplier entitlement in the product editor.
 *
 * The SERVER decides entitlement. ``/api/vendor/getVendorList/`` resolves
 * the tier list and the per-distributor DDP override against the effective
 * distributor identity and stamps each vendor row with ``is_available`` and
 * ``unavailable_reason``. Nothing here decides anything -- this module only
 * turns that decision into something a human can see, and stops the editor
 * from auto-selecting a supplier the server has already excluded.
 *
 * That split matters: a frontend filter is a HINT, not enforcement. Anyone
 * can call the API directly, so the gate that counts is the one in
 * ``api/src/endpoints/vendor.py``.
 *
 * WHY EXCLUDED SUPPLIERS ARE HIDDEN, NOT LABELLED
 * -----------------------------------------------
 *
 * Jake, reversing his own earlier call (PR #3104, which built the
 * disabled-with-a-reason row this module used to return): "They should
 * simply be hidden. We shouldn't leak to distributors that there are
 * specific suppliers that aren't supported on their tier, at least for
 * now."
 *
 * Because the reason is LEAKAGE, the load-bearing filter is the SERVER's
 * (``endpoints/vendor.py`` drops the row for any scoped caller), so an
 * excluded name never reaches the browser. The filter here is a SECOND
 * line for a payload that still carries a flagged row -- an older backend,
 * or the unfiltered Super Admin view being rendered in a tenant surface.
 * It is defense in depth, not the gate.
 *
 * The cost of hiding is the ambiguity the old label prevented: a short or
 * empty list explains nothing. That is paid in ``SupplierSkuPanel.vue``,
 * which renders an empty-state sentence when no supplier is offered --
 * without naming what is hidden or revealing that a tier gate exists.
 */

import { isSanmarSupplierName } from '@/utils/isSanmarSupplier'

/** What a distributor is told when their plan excludes a supplier. */
const REASON_LABELS = {
  plan: 'Not available on your plan',
}

/**
 * True when the server has NOT excluded this supplier.
 *
 * Absence of the flag means available. A vendor payload predating the
 * entitlement fields must not have every supplier silently disabled --
 * that would recreate the empty-editor failure in the frontend after the
 * backend was careful to avoid it. Denial is explicit
 * (``is_available: false``) or it has not happened.
 */
export function isSupplierAvailable(supplier) {
  return supplier?.is_available !== false
}

/**
 * Human-readable explanation for an excluded supplier, else null.
 *
 * Falls back to a generic phrasing for a reason code this build does not
 * know, rather than rendering an empty marker: a new server-side reason
 * should degrade to a vague explanation, never to a silent one.
 */
export function unavailableLabelFor(supplier) {
  if (isSupplierAvailable(supplier)) return null
  return REASON_LABELS[supplier?.unavailable_reason] || 'Not available'
}

/**
 * Annotate supplier rows for the v-select.
 *
 * Returns new objects -- the source array is API data shared with other
 * computeds, and mutating it in place is how two surfaces end up
 * disagreeing about the same row.
 *
 * ``props`` is Vuetify's ``item-props`` channel: ``disabled`` there is what
 * actually makes the list item unpickable, rather than merely styled.
 */
export function decorateSuppliers(suppliers) {
  return (suppliers || []).map((supplier) => {
    const available = isSupplierAvailable(supplier)
    return {
      ...supplier,
      unavailableLabel: unavailableLabelFor(supplier),
      props: { disabled: !available },
    }
  })
}

/**
 * The smart supplier the editor may auto-select for a new product.
 *
 * The editor auto-picks a smart supplier so the common SanMar path needs
 * no clicks. Left unguarded that latches onto an entitlement-excluded row,
 * and the gate would be visible but not binding: the form would submit a
 * supplier the distributor may not sell from. Returns undefined when every
 * smart supplier is excluded -- the user then picks deliberately, and the
 * disabled rows explain why the obvious choice is not one.
 */
export function firstSelectableSmartSupplier(suppliers) {
  return (suppliers || []).find(
    (supplier) => supplier?.is_smart_supplier && isSupplierAvailable(supplier),
  )
}

/**
 * A stable option key for one vendor row.
 *
 * The canonical editor's picker is keyed by ``supplier_key`` because that
 * is the vocabulary everything downstream already speaks: entitlement
 * resolves keys, ``SUPPORTED_SUPPLIERS`` gates live inventory on
 * ``'sanmar'``, and the tf-deconetwork payload carries the key on the
 * wire. A picker keyed on the display name would have to translate at
 * every boundary, and the translation would be the thing that drifts.
 *
 * Keyless vendors (a distributor's own local supplier) get a synthetic
 * ``vendor:<id>`` key so they remain distinguishable and selectable
 * without inventing a ``supplier_key`` that would imply a
 * tf-deconetwork mapping which does not exist. Note this is a UI
 * identity only -- it is never sent anywhere as a supplier key.
 */
export function supplierOptionKey(supplier) {
  const key = (supplier?.supplier_key || '').trim().toLowerCase()
  if (key) return key
  return `vendor:${supplier?._jv?.id ?? supplier?.id ?? 'unknown'}`
}

/** The ``vendors.kind`` that means "blank-goods supplier". */
const BLANK_GOODS_SUPPLIER_KIND = 'vendor'

/**
 * Is this vendor row a BLANK-GOODS supplier?
 *
 * ``kind`` decides (Jake, 2026-10-08: "For now `kind` decides, so when
 * `kind=vendor` that means blank-goods supplier"). The legacy
 * ``is_supplier`` flag is not consulted: ``POST /api/digitizers/`` used to
 * default it to TRUE, which put "Artistic Punch (Digitixers)" (kind
 * ``digitizer``) in this picker in production.
 *
 * The browser-side twin of ``is_blank_goods_supplier`` in
 * ``api/src/models/vendor.py``, including its two vetoes: pick-pack has no
 * ``kind`` value (every pick-pack provider is kind ``vendor``), and a legacy
 * decorator can still carry kind ``vendor``.
 *
 * A row with NO ``kind`` (an older backend) falls back to ``is_supplier``
 * with the same vetoes -- the pre-``kind`` answer -- rather than hiding
 * every supplier the server sent.
 */
export function isBlankGoodsSupplier(vendor) {
  if (!vendor || vendor.is_decorator || vendor.is_pick_pack) return false
  if (vendor.kind == null) return Boolean(vendor.is_supplier)
  return vendor.kind === BLANK_GOODS_SUPPLIER_KIND
}

/**
 * Turn vendor rows into v-select items for the canonical product editor.
 *
 * Only BLANK-GOODS suppliers become options (see ``isBlankGoodsSupplier``)
 * -- decorators, digitizers and pick-pack vendors have no catalog to pick
 * a SKU from.
 *
 * An excluded supplier produces NO option: not a disabled one, not a
 * labelled one, none. See the module docstring for why, and note the
 * server has already dropped it for a scoped caller -- this filter is the
 * second line, not the gate.
 */
export function toSupplierOptions(vendors) {
  return (vendors || [])
    .filter((vendor) => isBlankGoodsSupplier(vendor))
    .filter((vendor) => isSupplierAvailable(vendor))
    .map((vendor) => ({
      value: supplierOptionKey(vendor),
      title: vendor?.name ?? '',
      // The `vendors.id` the option stands for. A SKU pick stages it as the
      // CampaignProduct's `supplier_id`, which is what the server keys on.
      vendorId: vendorIdOf(vendor),
    }))
}

/** A vendor row's numeric id, or null when the row carries none. */
function vendorIdOf(vendor) {
  const raw = vendor?._jv?.id ?? vendor?.id
  const id = Number.parseInt(raw, 10)
  return Number.isFinite(id) ? id : null
}

/**
 * ONE PREDICATE FOR "DOES THIS SUPPLIER HAVE A CATALOG INTEGRATION".
 * =================================================================
 *
 * This exists because the product editor asked that question FIVE times
 * and got THREE answers, and a distributor saw all three at once. With S&S
 * Activewear selected the Supplier & SKU panel rendered an orange "Unknown
 * SanMar SKU" error, a blue "S&S has no catalog integration" notice, and a
 * Live inventory card badged PROMOSTANDARDS - SANMAR showing SanMar sample
 * colors -- simultaneously.
 *
 * The five sites disagreed because each carried its own test:
 * ``SUPPORTED_SUPPLIERS.includes(key)`` in two, a hardcoded ``'SanMar'``
 * string in a third, ``isSanmarSupplierName(supplier_name)`` in a fourth,
 * and the meta fetch never asked at all -- its endpoint was interpolated
 * with a literal ``sanmar``. Adding a supplier meant finding all five.
 * Nobody did; S&S shipped and the panel contradicted itself.
 *
 * KEYS, NOT NAMES
 * ---------------
 *
 * The rival test that caused the most damage was name-based.
 * ``isSanmarSupplierName`` matches ``/sanmar/i`` against a DISPLAY NAME --
 * a tenant-editable label, so "[DEMO] SanMar" and a distributor's own
 * "SanMar West" both match, and it fails OPEN on null (deliberately, for
 * the Artifi coverage gate it was written for). A display name is not an
 * identity. ``supplier_key`` is: entitlement resolves keys, the catalog
 * and product routes validate keys, and the tf-deconetwork payload carries
 * the key on the wire. ``supplierOptionKey`` above already made the picker
 * speak keys for exactly this reason.
 *
 * WHY THIS SET, AND WHY IT IS NOT ``['sanmar']``
 * ---------------------------------------------
 *
 * The frontend's ``SUPPORTED_SUPPLIERS = ['sanmar']`` was STALE, not
 * merely duplicated. Verified against the backend rather than assumed:
 *
 *   * ``_CATALOG_SEARCHERS`` and ``_PRODUCT_COMPOSERS`` in
 *     ``api/src/endpoints/sanmar_feed.py`` both carry ``sanmar`` AND
 *     ``ss_activewear``.
 *   * ``promostandards_inventory.SUPPORTED_SUPPLIERS`` is
 *     ``frozenset({"sanmar", "ss_activewear"})`` -- S&S PromoStandards
 *     Inventory 2.0.0 landed in PR #2826.
 *
 * So S&S has a real catalog, real product detail, and real live inventory,
 * and the panel was declining to ask for any of it. The fix is to ask the
 * RIGHT supplier, not merely to stop asking the wrong one.
 *
 * ``register_suppliers.py`` said as much in a comment when it added S&S:
 * "the picker will now OFFER S&S, but SKU autocomplete is not wired for it
 * yet ... That is honest ... but it is a gap, not a finished integration."
 * This closes that gap.
 *
 * FAILS CLOSED
 * ------------
 *
 * An unresolved supplier is NOT integrated. This is the property that
 * stops the bug at its source: ``SupplierSkuPanel`` initialises its
 * ``supplier`` ref to a pre-load default and the vendor list lands a round
 * trip later, so anything evaluating in that window sees a supplier that
 * may not be the real one. Absence of a decision must not be read as
 * permission to fire a catalog request -- the exact opposite of
 * ``isSanmarSupplierName``'s fail-open contract, which is why the two must
 * never be swapped for one another.
 *
 * KEEP THIS IN SYNC WITH THE BACKEND. A key added here without a
 * corresponding adapter/composer/inventory entry produces a 404 or 501
 * that renders as a user-facing error -- which is the bug this replaced.
 */
export const CATALOG_INTEGRATED_SUPPLIER_KEYS = Object.freeze(
  // 'ss_activewear' was 'sns' until 2026-09-24. The key is what goes in the
  // `/api/supplier/<key>/...` path segment AND what `vendors.supplier_key`
  // holds, so this Set and the backend registry must move together -- a
  // mismatch here renders as a 404 on the product-meta fetch.
  new Set(['sanmar', 'ss_activewear']),
)

/**
 * True when this supplier key has a catalog integration behind it.
 *
 * Consumed by the product-meta fetch, the fallback notice, the live
 * inventory gate, and the inventory badge -- one answer, four surfaces.
 */
export function hasCatalogIntegration(supplierKey) {
  const key = (supplierKey ?? '').toString().trim().toLowerCase()
  if (!key) return false
  return CATALOG_INTEGRATED_SUPPLIER_KEYS.has(key)
}

/**
 * Supplier keys whose styles Artifi's tenant carries -- the products that may
 * ask the /api/artifi/* proxies (coverage, product views, previews).
 *
 * A DIFFERENT question from ``CATALOG_INTEGRATED_SUPPLIER_KEYS``, which only
 * happens to hold the same keys today. Artifi imported S&S styles under the
 * S&S style name with S&S SKUs as their variants (measured 2026-10-07 against
 * Website 445: 3001CVC, 3413), and the backend resolves those through our S&S
 * catalog (``artifi_variant_resolver._rebuild_from_supplier_catalog``). An S&S
 * style Artifi does not carry answers ``sku_not_found``, which every Artifi
 * composable already treats as a quiet "no preview" state.
 */
export const ARTIFI_PREVIEW_SUPPLIER_KEYS = Object.freeze(
  new Set(['sanmar', 'ss_activewear']),
)

/**
 * True when a product may ask Artifi for previews.
 *
 * Three states, published by ProductEditor (the only place holding the
 * vendor options) to the editor store:
 *
 *   resolved, key    -> the key alone decides: "S&S Activewear" passes,
 *                       a non-integrated vendor's key does not.
 *   resolved, no key -> false. The vendor list loaded and does not map this
 *                       supplier to a key (a name it does not carry, e.g. a
 *                       distributor's "SanMar West"), so it has no Artifi
 *                       catalog -- the name is not consulted.
 *   unresolved       -> the answer the name gave before keys existed,
 *                       ``isSanmarSupplierName``, which fails OPEN on an
 *                       absent name (the PC54 null-supplier case). This is
 *                       the window before the vendor list lands, and every
 *                       surface mounted outside the editor shell.
 */
export function artifiPreviewEligible({
  supplierKey,
  supplierKeyResolved = false,
  supplierName,
} = {}) {
  const key = (supplierKey ?? '').toString().trim().toLowerCase()
  if (key) return ARTIFI_PREVIEW_SUPPLIER_KEYS.has(key)
  if (supplierKeyResolved) return false
  return isSanmarSupplierName(supplierName)
}

/**
 * The display name for a supplier key, for user-facing copy.
 *
 * Resolved from the SERVER's own option list rather than a local map: the
 * name is the tenant's, and hardcoding one here would reintroduce the
 * literal this change is removing. Falls back to a neutral noun -- never
 * an empty string and never the raw key, both of which read as a bug, and
 * never "SanMar", which reads as a confident lie.
 */
export function supplierDisplayName(
  supplierKey,
  options,
  fallback = 'this supplier',
) {
  const key = (supplierKey ?? '').toString().trim().toLowerCase()
  if (!key) return fallback
  const match = (options || []).find((option) => option?.value === key)
  return match?.title || fallback
}

/**
 * Resolve a supplier DISPLAY NAME to its key, or null.
 *
 * THE ONE SANCTIONED NAME->KEY CROSSING. Everything else in this module
 * insists on keys, so this needs its justification stated rather than
 * assumed.
 *
 * A CampaignProduct persists its supplier as ``supplier_id`` (FK to
 * ``vendors``) plus ``supplier_name``, a denormalized copy of the vendor's
 * name; there is no ``supplier_key`` column on it. Callers should go
 * through ``supplierKeyForProduct`` below, which resolves the FK first.
 * The name is the fallback for rows that carry a name but no
 * ``supplier_id`` (older rows, and products whose supplier was typed).
 *
 * What makes this safe where ``isSanmarSupplierName`` is not:
 *
 *   * It CONVERTS rather than deciding. The result is a key, and the
 *     capability question is then asked of the key by
 *     ``hasCatalogIntegration``. No caller branches on the name itself.
 *   * It matches EXACTLY against the SERVER's own option titles, not by
 *     regex. ``isSanmarSupplierName`` tests ``/sanmar/i``, so a
 *     distributor's own "SanMar West" matches and its SKUs would be sent
 *     to SanMar's catalog. An exact match declines, which is the safe
 *     answer for an unrecognized name.
 *   * It returns null for an ABSENT name instead of defaulting to SanMar.
 *     ``isSanmarSupplierName`` returns true for null by design (fail-open,
 *     correct for the Artifi coverage gate it was written for), and that
 *     default is a large part of how unrelated SKUs reached the SanMar
 *     endpoint.
 *
 * Case- and whitespace-insensitive because the stored name is free text.
 */
export function supplierKeyForName(supplierName, options) {
  const name = (supplierName ?? '').toString().trim().toLowerCase()
  if (!name) return null
  const match = (options || []).find(
    (option) => (option?.title ?? '').trim().toLowerCase() === name,
  )
  return match?.value ?? null
}

/**
 * Resolve a CampaignProduct's supplier to an option key, or null.
 *
 * ``supplier_id`` is the identity the server keys on (color pruning, cost
 * resolution, the products dedup). When the row HAS one, it is the only
 * answer: an id that is not on offer (excluded by the plan, say) resolves
 * to null rather than to whichever offered vendor shares its display name,
 * because the next SKU pick would then stage that OTHER vendor's id. The
 * name (see ``supplierKeyForName``) is the fallback only for rows with no
 * ``supplier_id`` at all.
 */
export function supplierKeyForProduct(supplierId, supplierName, options) {
  if (supplierId == null || supplierId === '') {
    return supplierKeyForName(supplierName, options)
  }
  const id = Number.parseInt(supplierId, 10)
  const match = (options || []).find((option) => option?.vendorId === id)
  return match?.value ?? null
}
